/** Auth context: holds the signed-in user and exposes sign in / up / out. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError, api, tokenStore, type Level, type User } from '../api'

interface AuthValue {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (b: { email: string; password: string; display_name: string; target_level: Level }) => Promise<void>
  signOut: () => void
  setUser: (u: User) => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Restore the session on first paint if a token is already stored.
  useEffect(() => {
    let alive = true
    if (!tokenStore.access) {
      setLoading(false)
      return
    }
    api
      .me()
      .then((u) => alive && setUser(u))
      .catch((err) => {
        // Only a genuine auth failure should discard the token; a network blip
        // must not sign the user out.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          tokenStore.clear()
        }
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { user: u, tokens } = await api.login({ email, password })
    tokenStore.save(tokens)
    setUser(u)
  }, [])

  const signUp = useCallback<AuthValue['signUp']>(async (body) => {
    const { user: u, tokens } = await api.register(body)
    tokenStore.save(tokens)
    setUser(u)
  }, [])

  const signOut = useCallback(() => {
    tokenStore.clear()
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, signIn, signUp, signOut, setUser }),
    [user, loading, signIn, signUp, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
